import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Card, Form, Spinner, Table } from '@themesberg/react-bootstrap'
import { useCookies } from 'react-cookie'
import { getCurrentGamePeriods, updateCurrentGamePeriod } from '../../../controller/api/gamePeriod'

const GAME_LABELS = {
  keno: 'Keno',
  mega: 'Mega 6/45',
  power: 'Power 6/55',
  max3d: 'Max3D',
  max3dpro: 'Max3D Pro',
  lotto535: 'Lotto 5/35'
}

const ALLOWED_JACKPOT_GAMES = ['mega', 'power']

const CurrentPeriod = () => {
  const [cookies] = useCookies(['lottmekong'])
  const [periods, setPeriods] = useState([])
  const [loading, setLoading] = useState(false)
  const [updating, setUpdating] = useState({})
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const fetchCurrentPeriods = useCallback(async () => {
    setLoading(true)
    setError('')
    setSuccess('')
    try {
      const resp = await getCurrentGamePeriods(cookies.lottmekong)
      if (resp && resp.data) {
        const normalized = resp.data.map((item) => ({
          gameId: item.gameId,
          periodId: item.id,
          lastPeriodId: item.lastId,
          nextPeriodId: item.nextId,
          expiredTime: item.expiredTime || '',
          resultDate: item.resultDate || '',
          resultTime: item.resultTime || '',
          nextResultDate: item.nextResultDate || '',
          nextResultTime: item.nextResultTime || '',
          jackpot1: item.jackpot1 || '',
          jackpot2: item.jackpot2 || '',
        }))
        setPeriods(normalized)
      } else {
        setPeriods([])
        setError('Không thể lấy dữ liệu kỳ quay hiện tại')
      }
    } catch (error) {
      console.error('fetchCurrentPeriods', error)
      setPeriods([])
      setError('Có lỗi xảy ra khi lấy dữ liệu. Vui lòng thử lại.')
    } finally {
      setLoading(false)
    }
  }, [cookies.lottmekong])

  useEffect(() => {
    document.title = 'Kỳ quay hiện tại'
  }, [])

  useEffect(() => {
    fetchCurrentPeriods()
  }, [fetchCurrentPeriods])

  const handleFieldChange = useCallback((index, field, value) => {
    setPeriods((prev) => {
      const next = [...prev]
      next[index] = {
        ...next[index],
        [field]: value,
      }
      return next
    })
  }, [])

  const handleUpdate = useCallback(async (period) => {
    const updateKey = `${period.gameId}-${period.periodId}`
    setUpdating(prev => ({ ...prev, [updateKey]: true }))
    setError('')
    setSuccess('')

    const payload = {
      gameId: period.gameId,
      periodId: period.periodId,
      lastPeriodId: period.lastPeriodId,
      nextPeriodId: period.nextPeriodId,
      expiredTime: period.expiredTime,
      jackpot1: period.jackpot1,
      jackpot2: period.jackpot2,
    }

    if (!ALLOWED_JACKPOT_GAMES.includes(period.gameId)) {
      delete payload.jackpot1
    }

    if (period.gameId !== 'power') {
      delete payload.jackpot2
    }

    try {
      const resp = await updateCurrentGamePeriod(cookies.lottmekong, payload)

      if (resp && resp.status === 'success') {
        setSuccess(`Cập nhật thành công cho ${GAME_LABELS[period.gameId] || period.gameId}`)
        fetchCurrentPeriods()
      } else {
        setError(`Cập nhật thất bại cho ${GAME_LABELS[period.gameId] || period.gameId}. ${resp?.message || 'Vui lòng thử lại.'}`)
      }
    } catch (error) {
      console.error('handleUpdate', error)
      let errorMessage = `Cập nhật thất bại cho ${GAME_LABELS[period.gameId] || period.gameId}.`
      
      if (error.response?.data?.message) {
        errorMessage += ` ${error.response.data.message}`
      } else if (error.message) {
        errorMessage += ` ${error.message}`
      } else {
        errorMessage += ' Vui lòng thử lại.'
      }
      
      setError(errorMessage)
    } finally {
      setUpdating(prev => {
        const next = { ...prev }
        delete next[updateKey]
        return next
      })
    }
  }, [cookies.lottmekong, fetchCurrentPeriods])

  const isDisabled = useCallback((gameId) => {
    return !ALLOWED_JACKPOT_GAMES.includes(gameId)
  }, [])

  const renderJackpotField = useCallback((period, index, fieldKey, placeholder) => {
    const disabled = fieldKey === 'jackpot1' ? isDisabled(period.gameId) : period.gameId !== 'power'
    return (
      <Form.Control
        type="text"
        value={period[fieldKey]}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(event) => handleFieldChange(index, fieldKey, event.target.value)}
      />
    )
  }, [handleFieldChange, isDisabled])

  const tableBody = useMemo(() => {
    if (loading) {
      return (
        <tr>
          <td colSpan={7} className="text-center">
            <Spinner animation="border" variant="primary" />
          </td>
        </tr>
      )
    }

    if (!periods.length) {
      return (
        <tr>
          <td colSpan={7} className="text-center">
            Không có dữ liệu
          </td>
        </tr>
      )
    }

    return periods.map((period, index) => {
      const updateKey = `${period.gameId}-${period.periodId}`
      const isUpdating = updating[updateKey]
      
      return (
        <tr key={`${period.gameId}-${index}`}>
          <td className="text-center align-middle"><strong>{GAME_LABELS[period.gameId] || period.gameId}</strong></td>
          <td>
            <Form.Control
              type="text"
              value={period.lastPeriodId}
              onChange={(event) => handleFieldChange(index, 'lastPeriodId', event.target.value)}
            />
          </td>
          <td>
            <Form.Control
              type="text"
              value={period.periodId}
              onChange={(event) => handleFieldChange(index, 'periodId', event.target.value)}
            />
          </td>
          <td>
            <Form.Control
              type="text"
              value={period.nextPeriodId}
              onChange={(event) => handleFieldChange(index, 'nextPeriodId', event.target.value)}
            />
          </td>
          <td>
            {renderJackpotField(period, index, 'jackpot1', 'Jackpot 1')}
          </td>
          <td>
            {renderJackpotField(period, index, 'jackpot2', 'Jackpot 2')}
          </td>
          <td className="text-center">
            <Button 
              variant="primary" 
              disabled={isUpdating}
              onClick={() => handleUpdate(period)}
            >
              {isUpdating ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Đang cập nhật...
                </>
              ) : (
                'Cập nhật'
              )}
            </Button>
          </td>
        </tr>
      )
    })
  }, [handleFieldChange, handleUpdate, loading, periods, renderJackpotField])

  return (
    <Card border="light" className="shadow-sm">
      <Card.Header>
        <Card.Title>Kỳ quay hiện tại</Card.Title>
      </Card.Header>
      <Card.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}
        <Table responsive className="align-items-center table-bordered table-hover">
          <thead className="text-center">
            <tr>
              <th>Sản phẩm</th>
              <th>Kỳ trước</th>
              <th>Kỳ hiện tại</th>
              <th>Kỳ tiếp theo</th>
              <th>Jackpot 1</th>
              <th>Jackpot 2</th>
              <th>Hành động</th>
            </tr>
          </thead>
          <tbody>
            {tableBody}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  )
}

export default React.memo(CurrentPeriod)
